import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { SearchIcon, UserIcon } from '../components/Icons';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

export const AgentDirectoryPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAgents();
  }, []);

  const formatGenres = (genres: string[]) => {
    const genreMap: Record<string, string> = {
      FANTASY: 'Fantasy',
      SCIENCE_FICTION: 'Science Fiction',
      MYSTERY: 'Mystery',
      THRILLER: 'Thriller',
      ROMANCE: 'Romance',
      HISTORICAL_FICTION: 'Historical Fiction',
      LITERARY_FICTION: 'Literary Fiction',
      CONTEMPORARY: 'Contemporary Fiction',
      YOUNG_ADULT: 'Young Adult',
      MIDDLE_GRADE: 'Middle Grade',
      HORROR: 'Horror',
      WOMENS_FICTION: 'Women\'s Fiction',
      MEMOIR: 'Memoir',
    };
    return genres.map(g => genreMap[g] || g);
  };

  const fetchAgents = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.agents.list, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to fetch agents');
        setLoading(false);
        return;
      }

      const transformedAgents = data.agents.map((agent: any) => ({
        id: agent.id,
        name: agent.displayName || 'Unknown Agent',
        agency: agent.agencyName || 'Independent',
        profilePhoto: null,
        status: agent.acceptingQueries ? 'open' : 'closed',
        genres: formatGenres(agent.preferredGenres || []),
        mswlPreview: agent.manuscriptWishlist?.join('. ') || agent.bio || 'No MSWL provided',
        responseTime: agent.responseTime || '6-8 weeks',
        submissionGuidelines: 'Query letter + first pages',
        recentSales: 0, // Not available from backend yet
        clientCount: 0, // Not available from backend yet
      }));

      setAgents(transformedAgents);
      setLoading(false);
    } catch (err) {
      console.error('Fetch agents error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         agent.agency.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         agent.genres.some((g: string) => g.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesGenre = genreFilter === 'all' || agent.genres.some((g: string) =>
      g.toLowerCase().includes(genreFilter.toLowerCase())
    );
    const matchesStatus = statusFilter === 'all' || agent.status === statusFilter;
    return matchesSearch && matchesGenre && matchesStatus;
  });

  const openCount = agents.filter(a => a.status === 'open').length;
  const closedCount = agents.filter(a => a.status === 'closed').length;

  const getStatusBadge = (status: string) => {
    return status === 'open'
      ? <Badge variant="success">Open to Queries</Badge>
      : <Badge variant="default">Closed to Queries</Badge>;
  };

  const allGenres = ['all', 'Literary Fiction', 'Historical Fiction', 'Mystery', 'Thriller',
                     'Romance', 'Fantasy', 'Science Fiction', 'Horror', 'Young Adult', 'Memoir'];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Page Header */}
        <div style={{
          marginBottom: '40px',
        }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Agent Directory
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Discover literary agents seeking new clients
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading agents...
              </p>
            </div>
          </Card>
        )}

        {/* Error State */}
        {!loading && error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.error,
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchAgents}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Quick Stats */}
        {!loading && !error && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}>
          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Total Agents
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: '400',
              color: theme.colors.primary,
              fontFamily: theme.fonts.monospace,
            }}>
              {agents.length}
            </div>
          </Card>

          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Open to Queries
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: '400',
              color: theme.colors.success,
              fontFamily: theme.fonts.monospace,
            }}>
              {openCount}
            </div>
          </Card>

          <Card style={{ padding: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: theme.fonts.monospace,
            }}>
              Currently Closed
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: '400',
              color: theme.colors.textTertiary,
              fontFamily: theme.fonts.monospace,
            }}>
              {closedCount}
            </div>
          </Card>
        </div>
        )}

        {/* Search and Filters */}
        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '32px',
          flexWrap: 'wrap',
        }}>
          {/* Search */}
          <div style={{
            flex: '1',
            minWidth: '300px',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: theme.colors.textTertiary,
            }}>
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search by name, agency, or genre..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px 14px 48px',
                fontSize: '16px',
                border: `2px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.large,
                fontFamily: theme.fonts.primary,
                color: theme.colors.textPrimary,
                backgroundColor: theme.colors.white,
                outline: 'none',
              }}
            />
          </div>

          {/* Genre Filter */}
          <select
            value={genreFilter}
            onChange={(e) => setGenreFilter(e.target.value)}
            style={{
              padding: '14px 56px 14px 16px',
              fontSize: '16px',
              border: `2px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.large,
              fontFamily: theme.fonts.primary,
              color: theme.colors.textPrimary,
              backgroundColor: theme.colors.white,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            {allGenres.map(genre => (
              <option key={genre} value={genre}>
                {genre === 'all' ? 'All Genres' : genre}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '14px 56px 14px 16px',
              fontSize: '16px',
              border: `2px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.large,
              fontFamily: theme.fonts.primary,
              color: theme.colors.textPrimary,
              backgroundColor: theme.colors.white,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="all">All Status</option>
            <option value="open">Open to Queries</option>
            <option value="closed">Closed to Queries</option>
          </select>

          {/* View Toggle */}
          <div style={{
            display: 'flex',
            gap: '8px',
            backgroundColor: theme.colors.white,
            padding: '4px',
            borderRadius: theme.borderRadius.medium,
            border: `2px solid ${theme.colors.border}`,
          }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '10px 16px',
                border: 'none',
                borderRadius: theme.borderRadius.small,
                backgroundColor: viewMode === 'grid' ? theme.colors.primary : 'transparent',
                color: viewMode === 'grid' ? theme.colors.white : theme.colors.textSecondary,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '10px 16px',
                border: 'none',
                borderRadius: theme.borderRadius.small,
                backgroundColor: viewMode === 'list' ? theme.colors.primary : 'transparent',
                color: viewMode === 'list' ? theme.colors.white : theme.colors.textSecondary,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              List
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div style={{
          marginBottom: '24px',
          fontSize: '15px',
          color: theme.colors.textSecondary,
          fontFamily: theme.fonts.monospace,
        }}>
          Showing {filteredAgents.length} of {agents.length} agents
        </div>

        {/* Agents Grid/List */}
        {filteredAgents.length === 0 ? (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                No agents match your search criteria
              </p>
            </div>
          </Card>
        ) : (
          <div style={{
            display: viewMode === 'grid' ? 'grid' : 'flex',
            gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(350px, 1fr))' : 'none',
            ...(viewMode === 'list' && { flexDirection: 'column' as const }),
            gap: '24px',
          }}>
            {filteredAgents.map((agent) => (
              <Card key={agent.id} hoverable>
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: '16px',
                }}>
                  {/* Profile Photo */}
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: theme.colors.primaryLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: theme.colors.primary,
                  }}>
                    <UserIcon width={30} height={30} />
                  </div>

                  {/* Agent Info */}
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: '500',
                      marginBottom: '4px',
                      color: theme.colors.textPrimary,
                      cursor: 'pointer',
                    }}
                    onClick={() => window.location.href = `/agents/${agent.id}`}
                    >
                      {agent.name}
                    </h3>
                    <div style={{
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      marginBottom: '8px',
                    }}>
                      {agent.agency}
                    </div>
                    {getStatusBadge(agent.status)}
                  </div>
                </div>

                {/* Genres */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: theme.colors.textSecondary,
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}>
                    Represents
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                  }}>
                    {agent.genres.map((genre: string, index: number) => (
                      <Badge key={index} variant="primary" size="small">
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* MSWL Preview */}
                <div style={{
                  backgroundColor: theme.colors.backgroundAlt,
                  padding: '16px',
                  borderRadius: theme.borderRadius.medium,
                  marginBottom: '16px',
                }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: theme.colors.textSecondary,
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}>
                    Manuscript Wish List
                  </div>
                  <p style={{
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: theme.colors.textPrimary,
                  }}>
                    {agent.mswlPreview}
                  </p>
                </div>

                {/* Stats */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '12px',
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: theme.colors.backgroundAlt,
                  borderRadius: theme.borderRadius.small,
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '500',
                      color: theme.colors.primary,
                      fontFamily: theme.fonts.monospace,
                    }}>
                      {agent.clientCount}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: theme.colors.textSecondary,
                    }}>
                      Clients
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: '500',
                      color: theme.colors.primary,
                      fontFamily: theme.fonts.monospace,
                    }}>
                      {agent.recentSales}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: theme.colors.textSecondary,
                    }}>
                      Recent Sales
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: '500',
                      color: theme.colors.primary,
                      fontFamily: theme.fonts.monospace,
                    }}>
                      {agent.responseTime}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: theme.colors.textSecondary,
                    }}>
                      Response
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  paddingTop: '16px',
                  borderTop: `1px solid ${theme.colors.border}`,
                }}>
                  <Button
                    onClick={() => window.location.href = `/agents/${agent.id}`}
                    fullWidth
                  >
                    View Profile
                  </Button>
                  {agent.status === 'open' && (
                    <Button
                      variant="outline"
                      onClick={() => window.location.href = `/writer/query/new?agent=${agent.id}`}
                      fullWidth
                    >
                      Submit Query
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};
